import React, { useEffect } from 'react';
import { FormHelperText, Stack, TextField } from '@mui/material';
import { useIntl } from 'react-intl';
import { ContractsApi } from 'modules/contract/api';
import { commonMessages } from 'utils/commonMessages';
import { Contract } from 'modules/contract/types';
import { useForm, useWatch } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDefaultErrorHandler } from 'api';

interface FindContractStepActiveProps {
  completeStep(selectedContract: Contract): void,
}

interface FormData {
  contractNumber: string,
  clientNumber: string
}

interface FormErrors {
  noContractFound: string
}

export const FindContractStepActive: React.FC<FindContractStepActiveProps> = ({ completeStep }) => {
  const { formatMessage } = useIntl();
  const defaultErrorHandler = useDefaultErrorHandler();
  const { register, control, setError, clearErrors, formState: { errors, isSubmitting }, handleSubmit } = useForm<FormData & FormErrors>();
  const watchAll = useWatch({ control });

  useEffect(() => {
    if (errors.noContractFound) {
      clearErrors('noContractFound');
    }
  }, [clearErrors, errors, watchAll]);

  const onSubmit = (formData: FormData) =>
    ContractsApi
      .find(formData.contractNumber, formData.clientNumber)
      .then((contract) => {
        completeStep(contract);
      })
      .catch((e) => {
        if (e.statusCode === 404) {
          setError('noContractFound', {
            message: formatMessage({ id: 'FindContractStep.NoContractFound', defaultMessage: 'No contract was found with the given contract and client numbers' }),
          });
        }
        else
          defaultErrorHandler(e);
      });

  return (
    <Stack gap={3} marginLeft={7} marginTop={5}>
      <TextField
        {...register('contractNumber', { required: formatMessage(commonMessages.fieldIsRequired) })}
        autoComplete='contractNumber'
        variant="outlined"
        fullWidth
        disabled={isSubmitting}
        error={Boolean(errors.contractNumber)}
        helperText={errors.contractNumber?.message}
        label={formatMessage(commonMessages.contractNumber)} />
      <TextField
        {...register('clientNumber', { required: formatMessage(commonMessages.fieldIsRequired) })}
        autoComplete='clientNumber'
        variant="outlined"
        fullWidth
        disabled={isSubmitting}
        error={Boolean(errors.clientNumber)}
        helperText={errors.clientNumber?.message}
        label={formatMessage(commonMessages.clientNumber)} />
      <Stack gap={1}>
        {errors.noContractFound &&
          <Stack alignItems='flex-end'>
            <FormHelperText error>{errors.noContractFound?.message}</FormHelperText>
          </Stack>
        }
        <Stack direction='row' justifyContent='flex-end' alignItems='center'>
          <LoadingButton variant="contained" color='primary' loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {formatMessage(commonMessages.continue)}
          </LoadingButton>
        </Stack>
      </Stack >
    </Stack>
  );
};